import { Injectable } from '@angular/core';
import { FleetTrackerIconService } from './fleet-tracker-icon.service';
import { ColorIndex } from './model/enum.model';
import { MarkerwithLabel } from './model/marker-label.model';
import {
    DirectionalMarkerModel,
    FenceLayerModel,
    FenceModel,
    IconType,
    LayerIndex,
    LineLayerModel,
    MarkerLayerModel,
    MarkerModel
} from './model/models';

//
// Model with native markers.
//
export interface NativeMarkerLayer<T> extends MarkerLayerModel<T> {
    nativeMarkers: google.maps.Marker[];
    visible: boolean;
}

export interface NativePolylineLayer<T> {
    nativeMarkers: google.maps.Polyline[];
    visible: boolean;
}

export interface NativeMarkerLayerLive<T> extends MarkerLayerModel<T> {
    nativeMarkers: Map<string, google.maps.Marker>;
    visible: boolean;
}

//
// Model with native polylines.
//
// export interface NativeTraceLayer<T> extends TraceLayerModel<T> {
//     nativeTraces: google.maps.Polyline[];
// }

//
// Model with native markers.
//
// export interface NativeAddressLayer<T> extends AddressLayerModel<T> {
//     nativeMarkers: google.maps.Marker[];
//     visible: boolean;
// }

//
// Model with native polylines.
//
export interface NativeHistoryTraceLayer<T> extends LineLayerModel<T> {
    nativeTraces: google.maps.Polyline[];
    nativeMarkers: Map<string, MarkerwithLabel[]>;
}

//
// Model with native circles and polygons.
//
export interface NativeFenceLayer<T> extends FenceLayerModel<T> {
    // nativeCircles: google.maps.Circle[];
    nativePolygons: google.maps.Polygon[];
    nativePolylines?: google.maps.Polyline[];
}

// Model with native Geofence marker
export interface NativeFenceMarker<T> extends FenceLayerModel<T> {
    nativeMarkers: google.maps.Marker[];
}

//
// Provide a shape with a custom symbol polyline as its border.
// This is required as neither polygons nor circles support the
// use of symbols when rendering the border.
//
export interface ShapeWithBorder {
    shape: google.maps.Polygon | google.maps.Circle;
    border?: google.maps.Polyline;
}

@Injectable()
export class FleetTrackerUtilityService {

    private readonly _colors: Map<ColorIndex, string> = new Map([
        [ColorIndex.C00, '#FFC107'],
        [ColorIndex.C01, '#F44336'],
        [ColorIndex.C02, '#8BC34A'],
        [ColorIndex.C03, '#FFC107'],
        [ColorIndex.C04, '#673AB7'],
        [ColorIndex.C05, '#03A9F4'],
        [ColorIndex.C06, '#FF5722'],
        [ColorIndex.C07, '#E01E63'],
        [ColorIndex.C08, '#8BC34A'],
        [ColorIndex.C09, '#FFEB3B'],
        [ColorIndex.C10, '#3F51B5'],
        [ColorIndex.C11, '#03A9f4'],
        [ColorIndex.C12, '#795548'],
        [ColorIndex.C13, '#000000'],
        [ColorIndex.C14, '#CDDC39'],
        [ColorIndex.C15, '#FF9800'],
        [ColorIndex.C16, '#03A9F4'],
        [ColorIndex.C17, '#009688'],
        [ColorIndex.C18, '#607D8B']
    ]);
    private _padding = {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
    };

    constructor(
        private _iconService: FleetTrackerIconService
    ) {
    }

    //
    // Rotating Icons:
    //
    // Rotated icons have one icon for each 10 degrees of rotation. This results in 36 visually distinct icons.
    // That appears to provide sufficient visual resolution for human viewing. Rotating icons are arranged in a
    // 6 X 6 matrix. Moreover, rotating icons can have up to 18 distinct colors. We provide one matrix for each
    // color, so to index a rotated icon you first choose the color, then the rotation index. There are 18 color
    // enumerations in the ColorIndex enumeration.
    //
    // Fixed Icons:
    //
    // Fixed icons are simply a set of 1 X 1 matrices (no rotations) with color support just like rotations.

    toHexColor(index: ColorIndex): string {
        return this._colors.get(index);
    }

    //
    getMarkerIcon<T>(marker: MarkerModel<T>, iconMap: Map<IconType, google.maps.Icon[][]>) {
        let heading = (<DirectionalMarkerModel<T>>marker).heading; // discriminates rotating versus fixed icons

        if (heading !== undefined && heading <= 360) { // moving markers
            const rotationSize = 10; // degrees of rotation per each image
            heading = Math.floor(heading / rotationSize); // rotation index
            return this.getIcon(marker, iconMap, heading);
        } else { // static markers
            return this.getIcon(marker, iconMap);
        }
    }

    getMarkerIconLive<T>(marker: MarkerModel<T>) {

        let heading = (<DirectionalMarkerModel<T>>marker).heading; // discriminates rotating versus fixed icons
        if (heading && heading <= 360) { // moving markers
            const rotationSize = 10; // degrees of rotation per each image
            heading = Math.floor(heading / rotationSize); // rotation index
            heading = (heading === 0 || heading === 36) ? 0 : heading;
            return this._iconService.markerIcons.get(IconType[this.getIconType(marker, heading)]);
        } else { // static markers
            return this._iconService.markerIcons.get(IconType[this.getIconType(marker)]);
        }
    }

    getIconType<T>(marker: MarkerModel<T>, heading = 0): string {
        const type = marker.icon;
        const color = marker.color ? marker.color : ColorIndex.C00;
        switch (type) {
            case IconType.Live:
            case IconType.Trail:
                return 'Live_' + color + '_' + heading;
            case IconType.LiveIn:
                return 'Live_in_' + color + '_' + heading;
            case IconType.LiveOut:
                return 'Live_out_' + color + '_' + heading;
            case IconType.Idle:
                return 'Idle_' + color;
            case IconType.IdleIn:
                return 'Idle_in_' + color;
            case IconType.IdleOut:
                return 'Idle_out_' + color;
            case IconType.Stale:
                return 'Stale_' + color;
            case IconType.Reallystale:
                return 'Reallystale_' + color;
            case IconType.Stopped:
                return 'Stopped_' + color;
            case IconType.StoppedIn:
                return 'Stopped_in_' + color;
            case IconType.StoppedOut:
                return 'Stopped_out_' + color;
            case IconType.Asset:
                return 'asset';
            default:
                return 'Live_' + color + '_' + heading;
        }
    }

    generateCircle(radius: number, center: google.maps.LatLngLiteral): google.maps.LatLngLiteral[] {
        const points = [];
        const centerLatLng = new google.maps.LatLng(center);
        for (let i = 0; i <= 360; i++) {
            const point = google.maps.geometry.spherical.computeOffset(centerLatLng, radius, i);
            points.push({ lat: point.lat(), lng: point.lng() });
        }

        return points;
    }

    showZoomDependentLayers(zoom: number, markers: Map<LayerIndex, NativeMarkerLayer<any>>) {
        markers.forEach(layer => {
            if (layer && layer.zoom) {
                const visible = zoom >= layer.zoom;
                if (visible !== layer.visible) { // skip if no change
                    layer.visible = visible;
                    layer.nativeMarkers.forEach(marker => {
                        marker.setVisible(visible);
                    });
                }
            }
        });
    }

    //
    // Get the visibility state for the layer and apply to all of its markers.
    //
    showZoomDependentLayersLive(zoom: number, markers: Map<LayerIndex, NativeMarkerLayerLive<any>>) {
        markers.forEach(layer => {
            if (layer && layer.zoom) {
                const visible = zoom >= layer.zoom;
                if (visible !== layer.visible) { // skip if no change
                    layer.visible = visible;
                    layer.nativeMarkers.forEach(marker => {
                        marker.setVisible(visible);
                    });
                }
            }
        });
    }

    //
    // Get the visibility state for the layer and apply to all of its markers.
    //
    // showZoomDependentTraces(zoom: number, traces: Map<LayerIndex, NativeTraceLayer<any>>) {
    //     traces.forEach(layer => {
    //         if (layer && layer.zoom) { // ignore if undefined
    //             layer.nativeTraces.forEach(trace => {
    //                 trace.setVisible(zoom >= layer.zoom);
    //             });
    //         }
    //     });
    // }

    setFenceDefaults<T>(fence: FenceModel<T>) {

        if (fence.penColor === undefined) {
            fence.penColor = 'black';
        }
        if (fence.penWeight === undefined) {
            fence.penWeight = 1;
        }
        if (fence.penOpacity === undefined) {
            fence.penOpacity = 1;
        }
        if (fence.brushColor === undefined) {
            fence.brushColor = 'black';
        }
        if (fence.brushOpacity === undefined) {
            fence.brushOpacity = 0.25;
        }
        if (fence.penColor === undefined) {
            fence.penColor = 'black';
        }
    }

    positionToPixel(position: google.maps.LatLng, map: google.maps.Map): { x: number, y: number } {

        const projection = map.getProjection(); // get bounding box on map image
        const topRight = projection.fromLatLngToPoint(map.getBounds().getNorthEast());
        const bottomLeft = projection.fromLatLngToPoint(map.getBounds().getSouthWest());
        const scale = Math.pow(2, map.getZoom()); // each zoom factor squares the size

        // translate position to map image point
        const point = projection.fromLatLngToPoint(position);

        // compute the map image offset then scale it to the view
        const x = Math.floor((point.x - bottomLeft.x) * scale);
        const y = Math.floor((point.y - topRight.y) * scale);

        return { x: x, y: y }; // pixel coordinates
    }

    //
    // Use built-in projection facility to convert the geo-coordinates to pixel coordinates.
    //
    private getIcon<T>(marker: MarkerModel<T>, iconMap: Map<IconType, google.maps.Icon[][]>, heading = undefined) {
        const type = marker.icon;
        const color = marker.color ? marker.color : ColorIndex.C00;
        const unspecified = iconMap.get(IconType.UnspecifiedHeading)[0][color];
        const unsupported = iconMap.get(IconType.UnsupportedIcon)[0][0];
        const icons = iconMap.get(type);
        let icon = unsupported; // goal: populate with indexed icon

        if (type === IconType.Live || type === IconType.LiveLabeled || type === IconType.LiveInLabeled
            || type === IconType.LiveOutLabeled || type === IconType.Trail || type === IconType.LiveOut || type === IconType.LiveIn) {
            icon = heading !== undefined ? icons[color][this.getArrowDirection(heading)] : unspecified; // rotating position icon
        } else if (icons) {
            icon = icons[0][color]; // fixed position icon (a row of 1 X 1 matrices)
        }

        if (type === IconType.TripStartBlank || type === IconType.TripEndBlank) {
            icon.labelOrigin = new google.maps.Point(13, 13);
        } else {
            icon.labelOrigin = new google.maps.Point(51, 36);
        }

        return icon;
    }

    private getArrowDirection(heading: number): number {
        if (heading === 0 || heading === 36) {
            return 0;
        } else {
            return heading;
        }
    }


    //
    //    Replay support:
    //
    //    utility.dispatch(items, async (item) => {
    //        const milliseconds = process(item); // process asynchronously
    //       await utility.pause(milliseconds); // wait asynchronously
    //    });

    //
    // process each item with an asynchronous await.
    //
    public async dispatch(items, process) {
        for (let i = 0; i < items.length; i++) {
            await process(items[i], i);
        }
    }

    //
    // An awaitable asynchronous timer -- call at end of your "process" method.
    //
    public pause = (duration) => new Promise(resolve => setTimeout(resolve, duration));
}
